'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ViewController } from 'foundations-components/transitional/components/ViewController';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { H5 } from 'foundations-components/transitional/components/H5';
import { ONE_HOUR } from 'MeetingsBase/constants/dates';
import styled from 'foundations-theming/utils';
import { getCustomParams as getCustomParamsSelector, getEvent as getEventSelector, getPaidMeetingsAvailableSelector, getPaymentLinkInfo } from 'MeetingsLib/selectors/BookInfoSelectors';
import { setEventDuration } from 'MeetingsLib/redux/actions/FormActions';
import { HiddenAriaStatus } from 'MeetingsLib/components/decorative/HiddenAriaStatus';
import { getAvailableStartTimesForSelectedDate as getAvailableStartTimesForSelectedDateSelector } from 'MeetingsLib/selectors/AvailabilitySelectors';
import PaidMeetingPrice from '../../payments/PaidMeetingPrice';
import { getPaymentSuccessfulSelector, getPaidMeetingPrice } from '../../../selectors/PaymentStatusSelectors';

// Styled component to replicate _durationPicker.sass functionality
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDurationPicker = styled.div.withConfig({
  displayName: "DurationPicker__StyledDurationPicker",
  componentId: "i1a1t1-0"
})(["display:grid;button{width:33%;}", ""], props => props.isSingle && `
    button {
      width: 100%;
    }
  `);
class DurationPicker extends Component {
  constructor(props) {
    super(props);
    this.handleSetEventDuration = ({
      target: {
        value
      }
    }) => {
      this.props.setEventDuration(value);
      this.setState({
        tabIndex: -1
      });
    };
    this.getCompoundTime = (hours, minutes) => {
      const hourString = I18n.text('prebook.durationPicker.shortHourCount', {
        count: Math.floor(hours)
      });
      const minuteString = I18n.text('prebook.durationPicker.minuteCount', {
        count: minutes
      });
      return I18n.text('prebook.durationPicker.combination', {
        hours: hourString,
        minutes: minuteString
      });
    };
    this.getHours = hours => {
      return I18n.text('prebook.durationPicker.hourCount', {
        count: hours
      });
    };
    this.getMinutes = minutes => {
      return I18n.text('prebook.durationPicker.minuteCount', {
        count: minutes
      });
    };
    this.getDurationOptionText = duration => {
      const minutes = I18n.moment.duration(duration).asMinutes();
      if (duration >= ONE_HOUR) {
        const remainderMinutes = I18n.moment.duration(minutes % 60).valueOf();
        const hours = I18n.moment.duration(duration).asHours();
        if (remainderMinutes !== 0) {
          return this.getCompoundTime(hours, remainderMinutes);
        } else {
          return this.getHours(hours);
        }
      }
      return this.getMinutes(minutes);
    };
    this.getButtonGroupOptions = durationOptions => {
      return durationOptions.map(duration => ({
        text: this.getDurationOptionText(duration),
        value: duration
      })).toJS();
    };
    this.renderDurationPickerHeader = durationOptions => {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.durationPicker.header",
        options: {
          count: durationOptions.size
        }
      });
    };
    this.renderPaidMeetingHeader = () => {
      const {
        isPaidMeeting,
        paidMeetingInfo,
        paidMeetingPrice,
        paymentSuccessful
      } = this.props;
      if (!isPaidMeeting) {
        return null;
      }
      return /*#__PURE__*/_jsx(PaidMeetingPrice, {
        price: paidMeetingPrice,
        paid: paymentSuccessful,
        currency: paidMeetingInfo.currency
      });
    };
    this.renderDurationPicker = durationOptions => {
      const buttonGroupOptions = this.getButtonGroupOptions(durationOptions);
      const isSingleOption = durationOptions.size === 1;
      return /*#__PURE__*/_jsxs(TFlex, {
        flexGrow: "1",
        flexShrink: "0",
        flexBasis: "0",
        paddingBlockStart: "800",
        paddingInline: "800",
        inlineSize: "100%",
        direction: "column",
        display: "block",
        children: [this.renderPaidMeetingHeader(), /*#__PURE__*/_jsx(H5, {
          id: "meeting-duration",
          children: this.renderDurationPickerHeader(durationOptions)
        }), /*#__PURE__*/_jsx(StyledDurationPicker, {
          isSingle: isSingleOption,
          children: /*#__PURE__*/_jsx(ViewController, {
            id: "duration-select",
            tabIndex: this.state.tabIndex,
            "aria-labelledby": "meeting-duration",
            display: "inline-block",
            value: this.props.selectedDuration,
            onChange: this.handleSetEventDuration,
            defaultValue: durationOptions.first(),
            options: buttonGroupOptions,
            size: "small"
          })
        }), /*#__PURE__*/_jsx(HiddenAriaStatus, {
          role: "status",
          "aria-live": "polite",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "accessible.durationStatus",
            options: {
              selectedDuration: this.getDurationOptionText(this.props.selectedDuration),
              selectedDate: I18n.moment(this.props.selectedDate).format('MMMM Do')
            }
          })
        })]
      });
    };
    this.state = {
      tabIndex: -1
    };
  }
  componentDidUpdate(prevProps) {
    if (this.props.selectedDate !== prevProps.selectedDate && prevProps.selectedDate !== null) {
      this.setState({
        tabIndex: 0
      });
      const durationPicker = document.getElementById('duration-select');
      if (durationPicker) {
        durationPicker.focus();
      }
    }
  }
  render() {
    const {
      durationOptions
    } = this.props;
    if (!durationOptions || durationOptions.size === 0) {
      return null;
    }
    return this.renderDurationPicker(durationOptions);
  }
}
DurationPicker.propTypes = {
  // loaded
  durationOptions: PropTypes.instanceOf(List).isRequired,
  selectedDuration: PropTypes.number.isRequired,
  selectedDate: PropTypes.string,
  setEventDuration: PropTypes.func.isRequired,
  availableStartTimeOptions: PropTypes.instanceOf(List).isRequired,
  isPaidMeeting: PropTypes.bool,
  paidMeetingInfo: PropTypes.object,
  paymentSuccessful: PropTypes.bool,
  paidMeetingPrice: PropTypes.number
};
export default connect(state => ({
  // Rerenders component in response to translation
  activeLocale: state.activeLocale,
  selectedDuration: getEventSelector(state).duration,
  selectedDate: getEventSelector(state).dateString,
  durationOptions: getCustomParamsSelector(state).durations,
  availableStartTimeOptions: getAvailableStartTimesForSelectedDateSelector(state),
  isPaidMeeting: getPaidMeetingsAvailableSelector(state),
  paidMeetingInfo: getPaymentLinkInfo(state),
  paymentSuccessful: getPaymentSuccessfulSelector(state),
  paidMeetingPrice: getPaidMeetingPrice(state)
}), {
  setEventDuration
})(DurationPicker);